/**
 * Email service — thin client for the Vercel-hosted mail server.
 *
 * Architecture:
 *   - All transactional emails (registration, order confirmation, contact form,
 *     admin notifications, future flows) POST structured data to the mail
 *     server, which renders templates server-side and forwards via Resend.
 *   - Templates live on the server, so this file no longer carries HTML.
 *   - The Resend API key is held by the mail server, not exposed in the
 *     browser bundle.
 *   - Forgot/Reset Password is the ONLY email path that uses Firebase
 *     (Firebase Auth's built-in template — keeps reset links signed by
 *     Firebase and works on the Spark plan).
 *
 * Configuration:
 *   REACT_APP_MAIL_API_URL — base URL of the mail server (no trailing slash).
 *   Defaults to the production deployment.
 *
 * Adding a new email type:
 *   1. Add a template + endpoint to the mail server.
 *   2. Add a typed sender below that posts to that endpoint.
 */

// ─────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────
const MAIL_API_BASE = (
  process.env["REACT_APP_MAIL_API_URL"] ||
  "https://golden-mango-mail-server.vercel.app"
).replace(/\/+$/, "");

// ─────────────────────────────────────────────────────────────
// Types (kept stable so existing callers don't change)
// ─────────────────────────────────────────────────────────────
export interface SendEmailParams {
  to: string | string[];
  subject: string;
  html: string;
  replyTo?: string;
}

export interface SendEmailResult {
  success: true;
  data: any;
}

export interface OrderEmailParams {
  to: string;
  orderId: string;
  customerName: string;
  orderDetails: any;
}

export interface ContactEmailParams {
  name: string;
  email: string;
  subject: string;
  message: string;
  phone?: string;
  to?: string;
}

export interface VerificationEmailParams {
  to: string;
  name: string;
  /** Optional override for the post-verification redirect target. */
  continueUrl?: string;
}

export interface PasswordResetEmailParams {
  to: string;
}

// ─────────────────────────────────────────────────────────────
// Internal: HTTP helper
// ─────────────────────────────────────────────────────────────
async function postJson<T = any>(path: string, body: unknown): Promise<T> {
  const url = `${MAIL_API_BASE}${path}`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (networkError) {
    console.error("[emailService] Network error calling mail API:", {
      url,
      error: networkError,
    });
    throw networkError;
  }

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null);
    console.error("[emailService] Mail API error:", {
      url,
      status: response.status,
      statusText: response.statusText,
      errorPayload,
    });
    const message =
      errorPayload?.error ||
      `Mail API responded ${response.status} ${response.statusText}`;
    throw new Error(message);
  }

  return response.json();
}

// ─────────────────────────────────────────────────────────────
// Public senders — signatures preserved for existing callers
// ─────────────────────────────────────────────────────────────

/**
 * Generic Resend send. Use the dedicated senders below when possible — they
 * delegate template rendering to the server. This is for ad-hoc messages.
 */
export const sendEmail = async ({
  to,
  subject,
  html,
  replyTo,
}: SendEmailParams): Promise<SendEmailResult> => {
  const data = await postJson("/api/send-email", {
    to,
    subject,
    html,
    ...(replyTo ? { replyTo } : {}),
  });
  return { success: true, data };
};

export const sendOrderConfirmationEmail = async (
  data: OrderEmailParams
): Promise<SendEmailResult> => {
  const result = await postJson("/api/send-order", {
    to: data.to,
    orderId: data.orderId,
    customerName: data.customerName,
    orderDetails: data.orderDetails,
  });
  return { success: true, data: result };
};

export const sendContactFormEmail = async (
  data: ContactEmailParams
): Promise<SendEmailResult> => {
  const result = await postJson("/api/send-contact", {
    name: data.name,
    email: data.email,
    subject: data.subject,
    message: data.message,
    ...(data.phone ? { phone: data.phone } : {}),
    ...(data.to ? { to: data.to } : {}),
  });
  return { success: true, data: result };
};

export const sendVerificationEmailCustom = async (
  data: VerificationEmailParams
): Promise<SendEmailResult> => {
  // Send users to the site root after Firebase finishes verifying. By the
  // time they hit "Continue" on Firebase's verification page, the email is
  // already verified — landing on /verify-email would just be a redundant
  // hop. AuthContext picks up the updated emailVerified flag on next login
  // and syncs it to Firestore.
  const continueUrl = data.continueUrl || `${window.location.origin}/`;
  const result = await postJson("/api/send-verification", {
    to: data.to,
    name: data.name,
    continueUrl,
  });
  return { success: true, data: result };
};

/**
 * Password reset.
 * The mail server uses Firebase Admin SDK to mint a Firebase-signed reset
 * link, then sends a branded email via Resend. Firebase still validates the
 * link when the user clicks it, so security guarantees are unchanged.
 */
export const sendPasswordResetEmailCustom = async (
  data: PasswordResetEmailParams
): Promise<SendEmailResult> => {
  const result = await postJson("/api/send-password-reset", {
    to: data.to,
    continueUrl: `${window.location.origin}/login`,
  });
  return { success: true, data: result };
};
